import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { createApiClient, apiClient } from "../../api/apiClient";
import { formatUserName } from "../../utils/emailTouserName";

// Types
export interface ShiftHandOverData {
  Location_Selected: string;
  Shift_Number: string;
  Created_By: string;
  Modified_By: string;
  Created: string;
  Modified: string;
  Status: string;
  Notes: string;
  ID: number;
}

export interface ShiftHandOverRowData {
  ID: number;
  Title: string;
  Notes: string;
  Location_Name: string;
  Location_Name_Loc_ID: string | null;
  Walk_Type: string;
  Zone: string | null;
  Frequency: string | null;
  Created: string;
  Period: number;
  Week: number;
  Shift: string | null;
  SPARC_Category_Selected: string | null;
  Walk_Number: number;
  Modified: string;
  Created_By: string;
  Modified_By: string | null;
  status: string;
  // Maintain backward compatibility with existing code
  id?: number;
  shift?: string;
  details?: string;
  created?: string;
  by?: string;
  modified?: string;
  modifiedBy?: string;
  notes?: string;
  [key: string]: any;
}

export interface FilterOptions {
  shift: FilterItem[];
  status: FilterItem[];
}

export interface ShiftHandOverViewFilters {
  shiftNumber?: FilterItem[];
  status?: FilterItem[];
  createdBy?: FilterItem[];
  modifiedBy?: FilterItem[];
  date?: FilterItem[];
}

export interface FilterItem {
  key: string;
  label: string;
  options: { id: string; displayText: string }[];
}

export interface Attendee {
  id: string;
  displayName: string;
  userPrincipalName: string;
}

export interface AutocompleteEmployee {
  gpId: string;
  name: string;
  email: string;
  id: string;
  title: string;
  first_name: string;
  last_name: string;
}

export interface EmployeeAutocompleteResponse {
  message: string;
  count?: number;
  data:
    | AutocompleteEmployee[]
    | {
        processedData: AutocompleteEmployee[];
        count?: number;
      };
}

export interface ShiftOption {
  id: string;
  displayText: string;
}

export interface ShiftHandoverQuestion {
  id: string;
  questionText: string;
  category: string;
  material?: string; // PDF link field
  details?: string; // Details field for question info
  Pallet_Type?: string; // Pallet_Type field for second label
}

export interface ShiftHandoverImage {
  imageId: string;
  imageBase64: string;
  imagePreview: string;
  description: string;
  questionId: string;
}

export interface MiscObservationImage {
  imageUrl: string | null;
  fileName: string | null;
  description: string;
  index: number;
}

// Add error handling types
export interface ShiftHandoverError {
  id: string;
  apiName: string;
  message: string;
  timestamp: number;
  context?: any;
}

export interface CentralizedErrorState {
  errors: ShiftHandoverError[];
  activeError: ShiftHandoverError | null;
  isErrorModalVisible: boolean;
}

interface ShiftHandOverState {
  shiftHandOvers: ShiftHandOverData[];
  shiftHandOverRows: ShiftHandOverRowData[];
  filterOptions: FilterOptions;
  attendees: Attendee[];
  selectedAttendees: Attendee[];
  autocompleteAttendees: Attendee[];
  autocompleteLoading: boolean;
  autocompleteError: string | null;
  shiftOptions: ShiftOption[];
  selectedShift: ShiftOption | null;
  shiftHandoverQuestions: ShiftHandoverQuestion[];
  selectedQuestions: ShiftHandoverQuestion[];
  shiftHandoverImages: ShiftHandoverImage[];
  handoverState: "new" | "in-progress" | "completed";
  observationNotes: string;
  miscObservationImages: MiscObservationImage[];
  miscellaneousObservations: any;
  signatureFileName: string | null;
  signatureImageUrl: string | null;
  isSignatureUpdated: boolean; // Flag to track if signature was newly uploaded
  questionsLoaded: boolean;
  selectedFilters: {
    shift: string;
    status: string;
  };
  selectedShiftHandOverViewFilters: {
    shiftNumber: string;
    status: string;
    createdBy: string;
    modifiedBy: string;
    date: string;
  };
  shiftHandOverMetrics: {
    completed: number;
    inProgress: number;
    total: number;
  };
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  activeTab: string;
  selectedShiftHandOver: ShiftHandOverRowData | null;
  isValidating: boolean;
  // Add centralized error handling state
  centralizedErrors: CentralizedErrorState;
}

const initialState: ShiftHandOverState = {
  shiftHandOvers: [],
  shiftHandOverRows: [],
  filterOptions: {
    shift: [],
    status: [],
  },
  attendees: [],
  selectedAttendees: [],
  autocompleteAttendees: [],
  autocompleteLoading: false,
  autocompleteError: null,
  shiftHandoverQuestions: [],
  selectedQuestions: [],
  shiftHandoverImages: [],
  handoverState: "new",
  observationNotes: "",
  miscObservationImages: [],
  miscellaneousObservations: "",
  signatureFileName: null,
  signatureImageUrl: null,
  isSignatureUpdated: false, // Default to false, only set to true when a new signature is uploaded
  questionsLoaded: false,
  shiftOptions: [
    { id: "1st", displayText: "1st" },
    { id: "2nd", displayText: "2nd" },
    { id: "3rd", displayText: "3rd" },
  ],
  selectedShift: { id: "1st", displayText: "1st" }, // Default to 1st shift
  selectedFilters: {
    shift: "",
    status: "",
  },
  selectedShiftHandOverViewFilters: {
    shiftNumber: "",
    status: "",
    createdBy: "",
    modifiedBy: "",
    date: "",
  },
  shiftHandOverMetrics: {
    completed: 0,
    inProgress: 0,
    total: 0,
  },
  loading: false,
  error: null,
  successMessage: null,
  activeTab: "previous-handovers",
  selectedShiftHandOver: null,
  isValidating: false,
  // Initialize centralized error state
  centralizedErrors: {
    errors: [],
    activeError: null,
    isErrorModalVisible: false,
  },
};

// Async thunk for employee autocomplete
export const searchEmployeeAutocomplete = createAsyncThunk(
  "shiftHandOver/searchEmployeeAutocomplete",
  async (searchKey: string, { rejectWithValue }) => {
    try {
      // Create API client
      const api = createApiClient();

      // Build URL with search key
      const url = `/common/employees/autocomplete?key=${searchKey}`;

      // Make API call with search key as payload
      const response = await apiClient(api, url, "GET");

      return response;
    } catch (error: any) {
      console.error("Employee autocomplete search error:", error);
      
      // Handle different error types
      if (error.response?.data?.message) {
        return rejectWithValue(error.response.data.message);
      } else if (error.message) {
        return rejectWithValue(error.message);
      } else {
        return rejectWithValue("Failed to search employees");
      }
    }
  }
);

const shiftHandOverSlice = createSlice({
  name: "shiftHandOver",
  initialState,
  reducers: {
    setShiftHandOvers: (state, action: PayloadAction<ShiftHandOverData[]>) => {
      state.shiftHandOvers = action.payload;
    },
    setShiftHandOverRows: (
      state,
      action: PayloadAction<ShiftHandOverRowData[]>
    ) => {
      state.shiftHandOverRows = action.payload;
    },
    setFilterOptions: (state, action: PayloadAction<FilterOptions>) => {
      state.filterOptions = action.payload;
    },
    setSelectedFilters: (
      state,
      action: PayloadAction<{
        shift?: string;
        status?: string;
      }>
    ) => {
      state.selectedFilters = { ...state.selectedFilters, ...action.payload };
    },
    setShiftHandOverViewFilters: (
      state,
      action: PayloadAction<{
        shiftNumber?: string;
        status?: string;
        createdBy?: string;
        modifiedBy?: string;
        date?: string;
      }>
    ) => {
      state.selectedShiftHandOverViewFilters = {
        ...state.selectedShiftHandOverViewFilters,
        ...action.payload,
      };
    },
    setShiftHandOverMetrics: (
      state,
      action: PayloadAction<{
        completed: number;
        inProgress: number;
        total: number;
      }>
    ) => {
      state.shiftHandOverMetrics = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setSelectedShiftHandOver: (
      state,
      action: PayloadAction<ShiftHandOverRowData | null>
    ) => {
      state.selectedShiftHandOver = action.payload;
    },
    resetFilters: (state) => {
      state.selectedFilters = initialState.selectedFilters;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    setAttendees: (state, action: PayloadAction<Attendee[]>) => {
      state.attendees = action.payload;
    },
    setSelectedAttendees: (state, action: PayloadAction<Attendee[]>) => {
      state.selectedAttendees = action.payload;
    },
    setSelectedShift: (state, action: PayloadAction<ShiftOption>) => {
      state.selectedShift = action.payload;
    },
    addSelectedAttendee: (state, action: PayloadAction<Attendee>) => {
      state.selectedAttendees.push(action.payload);
    },
    removeSelectedAttendee: (state, action: PayloadAction<string>) => {
      state.selectedAttendees = state.selectedAttendees.filter(
        (attendee) => attendee.id !== action.payload
      );
    },
    setShiftHandoverQuestions: (
      state,
      action: PayloadAction<ShiftHandoverQuestion[]>
    ) => {
      state.shiftHandoverQuestions = action.payload;
      state.questionsLoaded = true;
    },
    setSelectedQuestions: (
      state,
      action: PayloadAction<ShiftHandoverQuestion[]>
    ) => {
      state.selectedQuestions = action.payload;
    },
    toggleShiftHandoverOption: (
      state,
      action: PayloadAction<ShiftHandoverQuestion>
    ) => {
      const questionIndex = state.selectedQuestions.findIndex(
        (question) => question.id === action.payload.id
      );

      if (questionIndex >= 0) {
        // If the question is already selected, remove it
        state.selectedQuestions = state.selectedQuestions.filter(
          (question) => question.id !== action.payload.id
        );
      } else {
        // If the question is not selected, add it
        state.selectedQuestions.push(action.payload);
      }
    },
    setHandoverState: (
      state,
      action: PayloadAction<"new" | "in-progress" | "completed">
    ) => {
      state.handoverState = action.payload;
    },
    setShiftHandoverImage: (
      state,
      action: PayloadAction<ShiftHandoverImage>
    ) => {
      // Find image by matching both imageId AND questionId
      // This allows multiple images per question, each with a unique imageId
      const imageIndex = state.shiftHandoverImages.findIndex(
        (img) =>
          img.imageId === action.payload.imageId &&
          img.questionId === action.payload.questionId
      );

      if (imageIndex >= 0) {
        // Update existing image
        state.shiftHandoverImages[imageIndex] = action.payload;
      } else {
        // Add new image
        state.shiftHandoverImages.push(action.payload);
      }
    },
    resetShiftHandoverImages: (state) => {
      state.shiftHandoverImages = [];
    },
    clearShiftHandoverImages: (state) => {
      // Clear image base64 data while preserving metadata
      state.shiftHandoverImages = state.shiftHandoverImages.map(img => ({
        ...img,
        imageBase64: '',
        imagePreview: ''
      }));
    },
    limitShiftHandoverImages: (state, action: PayloadAction<number>) => {
      const maxImages = action.payload || 10;
      if (state.shiftHandoverImages.length > maxImages) {
        state.shiftHandoverImages = state.shiftHandoverImages.slice(-maxImages);
      }
    },
    clearMiscObservationImages: (state) => {
      state.miscObservationImages = [];
    },
    clearShiftHandoverData: (state) => {
      // Clear large data while preserving essential state
      state.shiftHandoverImages = [];
      state.miscObservationImages = [];
      state.shiftHandoverQuestions = [];
      state.selectedQuestions = [];
      state.observationNotes = "";
      state.miscellaneousObservations = [];
    },
    setObservationNotes: (state, action: PayloadAction<string>) => {
      state.observationNotes = action.payload;
    },
    setMiscObservationImage: (
      state,
      action: PayloadAction<{ index: number; image: MiscObservationImage }>
    ) => {
      if (!state.miscObservationImages) {
        state.miscObservationImages = [];
      }
      state.miscObservationImages[action.payload.index] = action.payload.image;
    },
    setMiscellaneousObservations: (state, action: PayloadAction<any>) => {
      state.miscellaneousObservations = action.payload;
    },
    setSignatureFileName: (
      state,
      action: PayloadAction<{
        fileName: string | null;
        imageUrl?: string | null;
        isNewUpload?: boolean; // Flag to indicate if this is a new upload or loading from API
      }>
    ) => {
      console.log(
        "Redux: Setting signature fileName:",
        action.payload.fileName,
        "imageUrl:",
        action.payload.imageUrl
      );
      state.signatureFileName = action.payload.fileName;
      state.signatureImageUrl = action.payload.imageUrl || null;
      // Only set isSignatureUpdated to true if this is a new upload
      if (action.payload.isNewUpload) {
        state.isSignatureUpdated = true;
        console.log("Redux: Signature has been newly uploaded");
      } else if (action.payload.isNewUpload === false) {
        // Explicitly set to false when loading from API
        state.isSignatureUpdated = false;
        console.log("Redux: Signature loaded from API, not marking as updated");
      }
      console.log("Redux: State after update:", {
        signatureFileName: state.signatureFileName,
        signatureImageUrl: state.signatureImageUrl,
        isSignatureUpdated: state.isSignatureUpdated,
      });
    },

    resetSignatureUpdatedFlag: (state) => {
      // Add a dedicated action to reset the signature updated flag
      state.isSignatureUpdated = false;
      console.log("Redux: Reset isSignatureUpdated flag to false");
    },
    resetShiftHandoverState: (state) => {
      // Preserve the values we want to keep
      const preservedValues = {
        selectedShiftHandOverViewFilters:
          state.selectedShiftHandOverViewFilters,
        shiftHandOverMetrics: state.shiftHandOverMetrics,
        attendees: state.attendees,
        successMessage: state.successMessage, // Preserve success message
      };

      // Reset state to initial values
      Object.assign(state, initialState);

      // Restore the preserved values
      state.selectedShiftHandOverViewFilters =
        preservedValues.selectedShiftHandOverViewFilters;
      state.shiftHandOverMetrics = preservedValues.shiftHandOverMetrics;
      state.attendees = preservedValues.attendees;
      state.successMessage = preservedValues.successMessage; // Restore success message

      // Questions are reset to empty (not preserved) so they can be re-fetched
      // with correct org context params when creating a new handover
      state.shiftHandoverQuestions = [];
      state.questionsLoaded = false;
    },
    setValidating: (state, action: PayloadAction<boolean>) => {
      state.isValidating = action.payload;
    },
    setAutocompleteAttendees: (state, action: PayloadAction<Attendee[]>) => {
      state.autocompleteAttendees = action.payload;
    },
    clearAutocompleteAttendees: (state) => {
      state.autocompleteAttendees = [];
      state.autocompleteError = null;
    },
    // Centralized error handling actions
    addCentralizedError: (state, action: PayloadAction<ShiftHandoverError>) => {
      state.centralizedErrors.errors.push(action.payload);
      state.centralizedErrors.activeError = action.payload;
      state.centralizedErrors.isErrorModalVisible = true;
    },
    showErrorModal: (state) => {
      state.centralizedErrors.isErrorModalVisible = true;
    },
    hideErrorModal: (state) => {
      state.centralizedErrors.isErrorModalVisible = false;
    },
    setActiveError: (
      state,
      action: PayloadAction<ShiftHandoverError | null>
    ) => {
      state.centralizedErrors.activeError = action.payload;
    },
    clearAllErrors: (state) => {
      state.centralizedErrors.errors = [];
      state.centralizedErrors.activeError = null;
      state.centralizedErrors.isErrorModalVisible = false;
    },
    removeErrorById: (state, action: PayloadAction<string>) => {
      state.centralizedErrors.errors = state.centralizedErrors.errors.filter(
        (error) => error.id !== action.payload
      );
      // If the removed error was the active one, clear it
      if (state.centralizedErrors.activeError?.id === action.payload) {
        state.centralizedErrors.activeError = null;
      }
    },
  },
  extraReducers: (builder) => {
    // Handle searchEmployeeAutocomplete async thunk
    builder
      .addCase(searchEmployeeAutocomplete.pending, (state) => {
        state.autocompleteLoading = true;
        state.autocompleteError = null;
      })
      .addCase(searchEmployeeAutocomplete.fulfilled, (state, action) => {
        state.autocompleteLoading = false;
        state.autocompleteError = null;
        
        // Transform API response to Attendee format
        const responseData = action.payload?.data;
        const employees = Array.isArray(responseData)
          ? responseData
          : responseData?.processedData || [];
        state.autocompleteAttendees = employees.map((emp: AutocompleteEmployee) => {
            let displayName = "";
            if (emp.first_name && emp.last_name && emp.first_name.trim() !== "" && emp.last_name.trim() !== "") {
            displayName = `${emp.first_name} ${emp.last_name}`.trim();
            } else if (emp.name && emp.name.trim() !== "") {
            // Remove anything in curly braces and trim whitespace
            displayName = emp.name.replace(/\s*\{.*?\}\s*$/, "").trim();
            } else {
            displayName = formatUserName(emp.email);
            }
          return {
            id: emp.id,
            displayName,
            userPrincipalName: emp.email,
          };
        });
      })
      .addCase(searchEmployeeAutocomplete.rejected, (state, action) => {
        state.autocompleteLoading = false;
        state.autocompleteError = action.payload as string || "Failed to search employees";
        state.autocompleteAttendees = [];
      });
  },
});

export const {
  setShiftHandOvers,
  setShiftHandOverRows,
  setFilterOptions,
  setSelectedFilters,
  setShiftHandOverViewFilters,
  setLoading,
  setError,
  setActiveTab,
  setSelectedShiftHandOver,
  setShiftHandOverMetrics,
  resetFilters,
  clearError,
  setSuccessMessage,
  clearSuccessMessage,
  setAttendees,
  setSelectedAttendees,
  setSelectedShift,
  addSelectedAttendee,
  removeSelectedAttendee,
  setShiftHandoverQuestions,
  setSelectedQuestions,
  toggleShiftHandoverOption,
  setHandoverState,
  setShiftHandoverImage,
  resetShiftHandoverImages,
  clearShiftHandoverImages,
  limitShiftHandoverImages,
  clearMiscObservationImages,
  clearShiftHandoverData,
  setObservationNotes,
  setMiscObservationImage,
  setMiscellaneousObservations,
  setSignatureFileName,
  resetSignatureUpdatedFlag,
  resetShiftHandoverState,
  setValidating,
  setAutocompleteAttendees,
  clearAutocompleteAttendees,
  // Export centralized error handling actions
  addCentralizedError,
  showErrorModal,
  hideErrorModal,
  setActiveError,
  clearAllErrors,
  removeErrorById,
} = shiftHandOverSlice.actions;

export default shiftHandOverSlice.reducer;
